<template>
    <div class="JobId pb-8 sm:pb-12 xl:pb-20">
        <PreviewBanner
            v-if="job && data?.preview"
            :id="job.id"
            :can-share="data.preview.canShare"
            label="táto ponuka ešte nie je zverejnená."
            type="job"
        />
        <div v-if="job" class="relative">
           <JobView v-if="job && org" :job="job" :org="org" :preview="!!data?.preview" />

            <OrgOtherJobs v-if="job.orgId && org" :exclude-id="job.id" :org-id="job.orgId" :org-title="org.title" />

            <RecommendedItems
                v-if="job && isProd"
                :id="job.id"
                collection="jobs"
                class="container"
                :lang="job.lang"
                :max-items-to-show="4"
                no-separator
            />
        </div>
    </div>
</template>

<script setup lang="ts">
import { countries } from '#shared/constants/cities'
import { EntityType } from '#shared/types/EntityType'
import { FormOfCollaborationType, type Job, JobContentMode, JobPlaceType, JobType } from '#shared/types/Job'
import { type Org, OrgType } from '#shared/types/Org'
import { VisibilityType } from '#shared/types/visibility'
import { formatJobType } from '#shared/utils/job'
import { OverviewType } from '#shared/types/Overview'
import JobView from '@/components/jobs/JobView.vue'
import RecommendedItems from '@/components/common/RecommendedItems.vue'
import { useJsonLd } from '@/composables/head/useJsonLd'
import { useSharing } from '@/composables/sharing/useSharing'
import { useAuthFetch } from '@/composables/useAuthFetch'
import PreviewBanner from '@/components/common/PreviewBanner.vue'
import OrgOtherJobs from '@/components/jobs/OrgOtherJobs.vue'
import { isProduction } from '@/utils/helpers'
import { isJobUnavailable } from '@/utils/jobAvailability'
import {
    absoluteUrl,
    buildBreadcrumbList,
    buildJobLocations,
    capitalizeFirst,
    collapseWhitespace,
    composeDescription,
    escapeHtml,
    htmlToPlainText,
    normalizeWebsiteUrl,
    REMOTE_JOB_IN_SLOVAKIA,
    SECTION_SOCIAL_IMAGES,
    toIsoDateTime,
    withSiteName
} from '@/utils/structuredData'

const emit = defineEmits<{
  addInfoMessage: [message: string]
  trackJobView: [data: { eventCategory: string, eventAction: string }]
}>()

const { getSharingUrl } = useSharing()

definePageMeta({
    layout: 'fullwidth'
})

const route = useRoute()
const router = useRouter()
const { t: $t } = useI18n()

// Data
const isProd = isProduction()
const jobSlug = computed(() => route.params.slug as string)

type JobDetailResponse = {
    job: Job | null
    org: Org | null
    /** Draft job the requester is not allowed to see (yet) — SSR requests carry no auth. */
    locked?: boolean
    /** Set for drafts the requester can see, via preview link or as a manager. */
    preview?: { canShare: boolean } | null
}

const previewToken = computed(() => (typeof route.query.preview === 'string' ? route.query.preview : undefined))

// Fetch data using server API (works on both SSR and client)
const { data, error, status } = await useFetch<JobDetailResponse>(`/api/jobs/${jobSlug.value}`, {
    key: `job-detail-${jobSlug.value}`,
    query: { preview: previewToken }
})

const { authFetch } = useAuthFetch()

function redirectToUnavailable() {
    return navigateTo({ path: '/ponuka-nie-je-aktualne-dostupna',
        query: {
            type: OverviewType.JOBS,
            id: jobSlug.value
        }
    }, { replace: true })
}

// Drafts come back locked from SSR (no auth there). Org members and admins get them on the
// client by retrying with their ID token; everyone else is sent to the "not available" page.
onMounted(async () => {
    if (!data.value?.locked) return
    try {
        const response = await authFetch<JobDetailResponse>(`/api/jobs/${jobSlug.value}`, {
            query: { preview: previewToken.value }
        })
        if (response.locked) {
            redirectToUnavailable()
            return
        }
        data.value = response
    } catch (fetchError) {
        console.error('[praca/slug] Failed to load draft job:', fetchError)
        redirectToUnavailable()
    }
})

// Handle hidden visibility redirect
if (data.value?.job && !data.value.preview && isJobUnavailable(data.value.job)) {
    await redirectToUnavailable()
}

// Handle error
if (error.value) {
    if (error.value.statusCode === 404) {
        await redirectToUnavailable()
    } else {
        throw createError({
            statusCode: error.value.statusCode || 500,
            message: error.value.message || 'Job could not be loaded'
        })
    }
}

// The job view translates its labels with $t(key, job.lang)
await useContentLocaleMessages(() => data.value?.job?.lang)

// Computed properties using data directly
const job = computed(() => data.value?.job ?? undefined)
const org = computed(() => data.value?.org)

const positionTitle = computed(() => collapseWhitespace(job.value?.title))
const orgName = computed(() => collapseWhitespace(job.value?.orgMeta?.title || org.value?.title))
const jobLang = computed(() => job.value?.lang || 'sk')

// "<Pozícia> – <Organizácia> | GROWNi.sk"; the organization is left out when the position already names it.
const jobTitle = computed(() => {
    const position = capitalizeFirst(positionTitle.value.replace(/\.+$/, ''))
    const showOrg = orgName.value && !position.toLowerCase().includes(orgName.value.toLowerCase())
    return withSiteName(showOrg ? `${position} – ${orgName.value}` : position)
})

const typeFormatted = computed(() => {
    return formatJobType(job.value)
})

const isRemoteJob = computed(() => !!job.value?.jobPlace?.includes(JobPlaceType.homeoffice))

const jobLocationText = computed(() => {
    const towns = buildJobLocations(job.value?.jobLocation)
        .map((place) => (place.address as { addressLocality?: string }).addressLocality)
        .filter(Boolean)
        .slice(0, 3)
        .join(', ')
    if (!isRemoteJob.value) return towns
    return towns ? `${towns} alebo z domu` : 'práca z domu'
})

/** Rich-text sections in the order the page shows them, with their headings. */
const jobContentSections = computed(() => {
    const current = job.value
    if (!current) return []
    if (current.contentMode === JobContentMode.CUSTOM && current.content) {
        return [{ heading: '', html: current.content }]
    }
    return [
        { heading: '', html: current.highlights ? `<p>${escapeHtml(current.highlights)}</p>` : '' },
        { heading: $t('jobView.jobChallenge', jobLang.value), html: current.challenge },
        { heading: $t('jobView.jobDescription', jobLang.value), html: current.desc },
        { heading: $t('jobView.jobBenefits', jobLang.value), html: current.benefits },
        { heading: $t('jobView.jobAdditionalInfos', jobLang.value), html: current.additionalInfo }
    ]
        .filter((section) => htmlToPlainText(section.html))
        // Some fields hold plain text (rendered with v-html all the same); give them a paragraph.
        .map((section) => ({ ...section, html: /<[a-z][^>]*>/i.test(section.html!) ? section.html! : `<p>${section.html}</p>` }))
})

const jobDescription = computed(() => {
    const details = [typeFormatted.value, jobLocationText.value].filter(Boolean).join(', ')
    const lead = orgName.value
        ? `${orgName.value} hľadá: ${positionTitle.value}${details ? ` (${details})` : ''}.`
        : `Pracovná ponuka ${positionTitle.value}${details ? ` (${details})` : ''}.`
    const firstSection = jobContentSections.value.find((section) => section.html)
    return composeDescription([lead, htmlToPlainText(firstSection?.html)])
})

const shouldShowJobSupportInfo = computed(() => {
    const orgType = org.value?.type
    return orgType ? [OrgType.privateSector, OrgType.startUp].includes(orgType) : false
})

const jobImage = computed(() => {
    return job.value?.orgMeta.image ? getSharingUrl('view', job.value.orgMeta.image) : SECTION_SOCIAL_IMAGES.jobs
})

// Build canonical URL
const canonicalUrl = computed(() => {
    const slug = job.value?.slug || jobSlug.value
    return absoluteUrl(`/praca/${slug}`)
})

// Only published offers are indexed and marked up. Unlisted ones (the default before publishing)
// open by direct link but stay out of search; drafts and previews never get markup.
const isPublicJob = computed(() => {
    const current = job.value
    if (!current || data.value?.preview || data.value?.locked) return false
    return current.visibility === VisibilityType.published && !isJobUnavailable(current)
})

const EMPLOYMENT_TYPES: Record<string, string> = {
    [JobType.fullTime]: 'FULL_TIME',
    [JobType.partTime]: 'PART_TIME',
    [JobType.internship]: 'INTERN',
    [JobType.paidInternship]: 'INTERN',
    [JobType.unpaidInternship]: 'INTERN'
}

/** schema.org employmentType values for the job types, plus CONTRACTOR when invoicing is offered. */
function getEmploymentTypes(current: Job): string[] {
    // Old offers store a single type instead of an array (see historicJobAdapter).
    const jobTypes: string[] = Array.isArray(current.type) ? current.type : current.type ? [current.type] : []
    const types = jobTypes.map((type) => EMPLOYMENT_TYPES[type]).filter((type): type is string => !!type)
    if (current.formOfCollaboration?.includes(FormOfCollaborationType.invoice)) types.push('CONTRACTOR')
    return [...new Set(types)]
}

const SALARY_UNITS: Record<string, { unitText: string; min: number; max: number }> = {
    'EUR / hodinu': { unitText: 'HOUR', min: 2, max: 300 },
    'EUR / deň': { unitText: 'DAY', min: 20, max: 3000 },
    'EUR / mesiac': { unitText: 'MONTH', min: 200, max: 30000 }
}

/**
 * The salary the page shows, as a MonetaryAmount. Values that can't be meant for the unit
 * (e.g. "1 – 1500 € / mesiac", "1300 € / hodinu") are dropped rather than passed to Google.
 */
function getBaseSalary(current: Job) {
    const unit = SALARY_UNITS[current.salaryUnit || '']
    if (!unit) return undefined
    const [minValue, maxValue] = [current.salaryMin, current.salaryMax].map((value) => {
        const amount = Number(value)
        return amount >= unit.min && amount <= unit.max ? amount : undefined
    })
    if (minValue === undefined && maxValue === undefined) return undefined
    const amount =
        minValue !== undefined && maxValue !== undefined && minValue < maxValue
            ? { minValue, maxValue }
            : { value: minValue ?? maxValue }
    return {
        '@type': 'MonetaryAmount',
        currency: 'EUR',
        value: { '@type': 'QuantitativeValue', ...amount, unitText: unit.unitText }
    }
}

// https://developers.google.com/search/docs/appearance/structured-data/job-posting
const jobPostingJsonLd = computed(() => {
    const current = job.value
    if (!current || !isPublicJob.value) return null

    // Google needs a place unless the job is fully remote. Offers whose location is "Na dohode"
    // or a region still happen in Slovakia, which is all we know for sure.
    const listedPlaces = buildJobLocations(current.jobLocation, { countryNames: countries.map((country) => country.id) })
    const jobPlaces =
        listedPlaces.length || isRemoteJob.value
            ? listedPlaces
            : [{ '@type': 'Place', address: { '@type': 'PostalAddress', addressCountry: 'SK' } }]

    const description = jobContentSections.value
        .map((section) => (section.heading ? `<p><strong>${escapeHtml(section.heading)}</strong></p>` : '') + section.html)
        .join('')

    return {
        '@type': 'JobPosting',
        title: positionTitle.value,
        // Google requires an HTML description that differs from the title.
        description: description || `<p>${escapeHtml(jobDescription.value)}</p>`,
        datePosted: toIsoDateTime(current.publishedAt) || toIsoDateTime(current.createdAt),
        validThrough: toIsoDateTime(current.endDate),
        employmentType: getEmploymentTypes(current),
        hiringOrganization: {
            '@type': 'Organization',
            name: orgName.value,
            sameAs: normalizeWebsiteUrl(org.value?.web),
            logo: current.orgMeta?.image || org.value?.image
        },
        jobLocation: jobPlaces.length ? jobPlaces : undefined,
        ...(isRemoteJob.value ? REMOTE_JOB_IN_SLOVAKIA : {}),
        baseSalary: getBaseSalary(current),
        // Applying needs a GROWNi account with a verified e-mail, so it is not "direct apply".
        directApply: false,
        identifier: { '@type': 'PropertyValue', name: orgName.value || 'GROWNi', value: current.id },
        url: canonicalUrl.value
    }
})

useJsonLd(() => [
    jobPostingJsonLd.value,
    isPublicJob.value &&
        buildBreadcrumbList([
            { name: 'Domov', path: '/' },
            { name: 'Práca', path: '/praca' },
            { name: positionTitle.value, path: canonicalUrl.value }
        ])
])

// Meta data
const headMetaConfig = computed(() => {
    return {
        title: jobTitle.value,
        meta: [
            ...(data.value?.preview || data.value?.locked
                ? [{ name: 'robots', content: 'noindex, nofollow' }]
                : job.value && !isPublicJob.value
                  ? [{ name: 'robots', content: 'noindex, follow' }]
                  : []),
            { name: 'description', content: jobDescription.value },
            { property: 'og:title', content: jobTitle.value },
            { property: 'og:description', content: jobDescription.value },
            { property: 'og:image', content: jobImage.value },
            { property: 'og:image:alt', content: `Logo organizácie ${orgName.value || positionTitle.value}` },
            { property: 'og:type', content: 'website' },
            { property: 'og:url', content: canonicalUrl.value }
        ],
        script: [
            {
                key: 'gtm-job-page',
                innerHTML: `
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          'unique_id': '${job.value?.id || ''}',
          'modul_subtype': '${job.value?.type || ''}'
        });
      `,
                type: 'text/javascript' as const
            }
        ],
        link: [
            // `key` dedupes against the site-wide canonical set in app.vue
            { rel: 'canonical' as const, key: 'canonical', href: canonicalUrl.value }
        ]
    }
})

useHead(headMetaConfig)

// Set dynamic breadcrumb title when job data is available
watch(job, (newJob) => {
  if (newJob?.title && process.client) {
    nextTick(() => {
      const { $dynamicTitle } = useNuxtApp()
      if ($dynamicTitle) {
        $dynamicTitle(newJob.title)
      }
    })
  }
}, { immediate: true })

// Lifecycle hooks
onMounted(() => {
  if (process.client) {
    emit('trackJobView', {
      eventCategory: EntityType.Job,
      eventAction: 'view'
    })

    if (job.value && shouldShowJobSupportInfo.value) {
      emit('addInfoMessage', $t('jobView.project', job.value?.lang || 'sk'))
    }
  }
})
</script>
